/**
 * A shop — what the script still does for the buildings the server draws
 * (templates/site/components/_shop.html.twig): the award badge of the rail,
 * and the shop cat.
 *
 * @module components/shop
 */

import { $, $$, pad } from '../lib/dom.js';
import { html, raw, renderToString } from '../lib/html.js';

/**
 * The official badge of an award (project JSON: award.badge), as a link to
 * the award page.
 *
 * @param {{ label: string, href: string, badge?: string } | undefined} award
 * @param {'fixed' | 'sign'} placement - Fixed on the page edge, or hung on a facade.
 * @returns {import('../lib/html.js').Markup | ''}
 */
export function awardBadge(award, placement) {
    if (!award?.badge) {
        return '';
    }

    return html`<a class="award-badge award-badge--${placement}" href="${award.href}" target="_blank" rel="noopener">
        <img src="${award.badge}" alt="${award.label}" width="53" height="171">
    </a>`;
}

/**
 * Easter egg: a black cat lives in one of the shops, sitting in the warm
 * light behind the door — only seen when the door opens. It moves house
 * every day, and lives in the same shop on every page of that day.
 *
 * @param {number} total - Number of shops on the street.
 * @returns {number} Place of the cat's shop on the street.
 */
export function catHome(total) {
    const day = Math.floor(Date.now() / 86_400_000);
    return total > 0 ? day % total : -1;
}

/**
 * Lets the shop cat in, on a page the server wrote: into the door of the
 * shop numbered after today's home. The server leaves it out — a page held
 * in a shared cache would keep it in yesterday's shop.
 *
 * @param {number} total - Number of shops on the street.
 */
export function placeShopCat(total) {
    const number = pad(catHome(total));
    const home = $$('[data-shop]').find((item) => $('.shop__number', item)?.textContent === number);

    if (home) {
        $('.shop__door', home)?.insertAdjacentHTML('afterbegin', renderToString(shopCat()));
    }
}

/** The shop cat: sitting, blinking, its tail swaying. */
function shopCat() {
    return raw(
        '<svg class="shop-cat" viewBox="0 0 40 48" aria-hidden="true" focusable="false">' +
            '<path class="shop-cat__tail" d="M28 45 C37 45 39 35 34 29"/>' +
            '<path class="shop-cat__body" d="M8 47 C6 35 10 25 17 23 L23 23 C30 25 33 35 31 47 Z"/>' +
            '<path class="shop-cat__head" d="M10 14 L11 3 L16.5 8.5 L23.5 8.5 L29 3 L30 14 C31 21 26 25 20 25 C14 25 9 21 10 14 Z"/>' +
            '<g class="shop-cat__eyes"><ellipse cx="16" cy="15.5" rx="2" ry="2.5"/><ellipse cx="24" cy="15.5" rx="2" ry="2.5"/></g>' +
            '</svg>',
    );
}
